import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import RegulatoryCompliance from "./RegulatoryCompliance";

// Keep the authenticated app alive in the original window; no credentials in URLs.
export default function InspectorLauncher({
  token,
  userData,
  onClose
}) {
  const [branches, setBranches] = useState(null);
  const [branch, setBranch] = useState("");
  const [error, setError] = useState("");
  const [target, setTarget] = useState(null);
  const child = useRef(null);
  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      try {
        const response = await fetch(apiUrl("/api/headoffice/compliance-grid/load"), {
          method: "POST",
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: "{}"
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Could not load branches");
        setBranches(data.branches || []);
        setBranch(String(data.branches?.[0]?.dbName || ""));
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message);
      }
    }
    load();
    return () => controller.abort();
  }, [token]);
  useEffect(() => () => {
    if (child.current && !child.current.closed) child.current.close();
  }, []);
  useEffect(() => {
    if (!target) return undefined;
    const poll = window.setInterval(() => {
      if (child.current?.closed) onClose();
    }, 500);
    return () => window.clearInterval(poll);
  }, [target, onClose]);
  function open() {
    // Called directly by a click so the browser can allow the new window.
    const screen = window.screen;
    const width = screen.availWidth || window.outerWidth;
    const height = screen.availHeight || window.outerHeight;
    const left = Number.isFinite(screen.availLeft) ? screen.availLeft : 0;
    const top = Number.isFinite(screen.availTop) ? screen.availTop : 0;
    const popup = window.open("", "_blank", `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`);
    if (!popup) {
      setError("The browser blocked the inspection window. Allow popups for this site, then click Open Inspector view again.");
      return;
    }
    child.current = popup;
    // Browsers expose sizing rather than a native "maximise" command.
    // Use the available screen bounds, leaving the OS taskbar accessible.
    try {
      popup.moveTo(left, top);
      popup.resizeTo(width, height);
    } catch (_) {/* Browser window policy may restrict resizing. */}
    popup.document.title = "Regulatory compliance - inspection view";
    const base = popup.document.createElement("base");
    base.href = document.baseURI;
    popup.document.head.appendChild(base);
    document.querySelectorAll('link[rel="stylesheet"], style').forEach(el => popup.document.head.appendChild(el.cloneNode(true)));
    popup.document.body.style.margin = "0";
    const root = popup.document.createElement("div");
    popup.document.body.appendChild(root);
    setTarget(root);
    popup.focus();
  }
  if (target) return createPortal(<RegulatoryCompliance inspector initialBranch={branch} token={token} userData={userData} onClose={onClose} />, target);
  return <div role="dialog" aria-modal="true" aria-labelledby="inspector-branch-title" style={{
    position: "fixed",
    inset: 0,
    zIndex: 12000,
    background: "#0008",
    display: "grid",
    placeItems: "center"
  }}>
    <section style={{
      background: "white",
      borderRadius: 10,
      padding: 24,
      width: 420,
      maxWidth: "90vw"
    }}>
      <h2 id="inspector-branch-title">Choose branch for inspection</h2>
      <p>The inspection screen will open in a new browser window.</p>
      {!branches && !error && <p>Loading branches…</p>}
      {branches?.length === 0 && <p>No branches are available for this account.</p>}
      {branches?.length > 0 && <label>Branch <select aria-label="Choose inspection branch" style={{
          width: "100%",
          height: 48,
          minHeight: 48,
          boxSizing: "border-box",
          padding: "8px 12px",
          fontSize: 16,
          lineHeight: "normal",
          color: "#262626",
          backgroundColor: "#fff",
          margin: "10px 0 20px"
        }} value={branch} onChange={e => setBranch(e.target.value)}>{branches.map(b => <option key={b.dbName} value={String(b.dbName)}>{b.pharmacyName || b.dbName}</option>)}</select></label>}
      {error && <p role="alert" style={{
        color: "#A8071A"
      }}>{error}</p>}
      <button onClick={onClose}>Cancel</button>{" "}<button disabled={!branch} onClick={open}>Open Inspector view ↗</button>
    </section>
  </div>;
}
